// types/KanbanTask.ts
import dayjs, { Dayjs } from 'dayjs';
// stores/KanbanTaskStore.ts
import { makeAutoObservable, runInAction } from "mobx";
import ApiStore from "./ApiStore";
import { Attachment, ScheduledAndPriorityUpdate, TaskTimeLog, TaskTimeMetrics } from '../api/KanbanTaskApiClient';
import { OutputData } from '@editorjs/editorjs';
import { Employee } from '../models/Employee';

export type KanbanTaskStatus = {
  id: string;
  name: string;
  index: number;
  projectId: string;
  isFinalStatus: boolean;
};

export type KanbanTask = {
  id: string;
  publicId?: string;
  title: string;
  description: string;
  summary: string;
  statusId: string;
  priority: number;
  index: number;
  projectId: string;
  assigneeId?: string;
  createdByUserId: string;
  parentTaskId?: string;
  createdDate: Dayjs;
  updatedDate: Dayjs;
  startDate?: Dayjs;
  dueDate?: Dayjs;
  estimatedTime?: string;
  modifiedDate: Dayjs; // Contains the date of the last task modification (includ action with task and comments for it)
  assignee?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    photoId?: string;
  } | Employee;
  createdByUser?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    photoId?: string;
  };
  status?: KanbanTaskStatus;
  comments?: KanbanTaskComment[];
  subTasks?: KanbanTask[];
}

export type KanbanTaskComment = {
  id: string;
  content: string;
  taskId: string;
  authorId: string;
  createdDate: Dayjs;
  author?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    photoId?: string;
  };
}

export type NewKanbanTask = {
  title: string;
  description: string;
  projectId: number;
  statusId: number;
  assigneeId?: number;
  parentTaskId?: number;
  priority: number;
  createdByUserId: number;
  startDate?: string;
  dueDate?: string;
  summary?: string;
}

export type NewKanbanTaskComment = {
  content: string;
  taskId: string;
  authorId?: number;
}

export type ExportTimeLogsParams = {
  userId: string;
  projectId: string;
  startPeriod?: string;
  endPeriod?: string;
  exportAllLogs?: boolean;
  language?: string;
}

export function getPlainTextFromEditorData(input: unknown): string {
  let data: any = input;
  if (typeof input === 'string') {
    const s = input.trim();
    if ((s.startsWith('{') && s.endsWith('}')) || (s.startsWith('[') && s.endsWith(']'))) {
      try { data = JSON.parse(s); } catch { /* leave as string */ }
    }
  }

  const decodeEntities = (raw: string): string => {
    if (!raw) return '';
    let s = raw
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");

    s = s.replace(/&#(\d+);/g, (_, d) => String.fromCharCode(parseInt(d, 10)));
    s = s.replace(/&#x([0-9a-fA-F]+);/g, (_, h) => String.fromCharCode(parseInt(h, 16)));
    return s;
  };

  const stripHtml = (html: string): string =>
    decodeEntities(html.replace(/<[^>]*>/g, ' '));

  const normalize = (s: string): string =>
    s
      .replace(/[•●▪◦\u2022\u25CF\u25AA\u25E6]/g, ' ')
      .replace(/[\t\r\n]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const push = (arr: string[], maybe: unknown) => {
    if (typeof maybe !== 'string') return;
    const clean = normalize(stripHtml(maybe));
    if (clean) arr.push(clean);
  };

  const out: string[] = [];

  if (data && typeof data === 'object' && Array.isArray((data as OutputData).blocks)) {
    for (const b of (data as OutputData).blocks) {
      const type = b?.type;
      const d: any = b?.data ?? {};

      switch (type) {
        case 'paragraph':
        case 'header':
        case 'quote':
        case 'warning':
          push(out, d.text);
          break;

        case 'list': {
          if (Array.isArray(d.items)) {
            push(out, d.items.join(' '));
          }
          break;
        }

        case 'checklist': {
          if (Array.isArray(d.items)) {
            push(out, d.items.map((it: any) => it?.text ?? '').join(' '));
          }
          break;
        }

        case 'table': {
          if (Array.isArray(d.content)) {
            for (const row of d.content) {
              if (Array.isArray(row)) push(out, row.join(' '));
            }
          }
          break;
        }

        case 'code':
          push(out, d.code);
          break;

        case 'image':
        case 'embed':
        case 'delimiter':
          break;

        default:
          if (typeof d.text === 'string') push(out, d.text);
          break;
      }
    }
    return normalize(out.join(' '));
  }

  if (typeof input === 'string') {
    return normalize(stripHtml(input));
  }

  try {
    return normalize(stripHtml(JSON.stringify(input)));
  } catch {
    return '';
  }
}

export function makeSummary(input: unknown, limit = 100): string {
  const plain = getPlainTextFromEditorData(input);
  if (plain.length <= limit) return plain;
  const soft = plain.slice(0, limit + 1);
  const lastSpace = soft.lastIndexOf(' ');
  return (lastSpace > 50 ? soft.slice(0, lastSpace) : plain.slice(0, limit)).trim();
}

export type KanbanTaskChange = {
  id: string;
  propertyName?: string;
  previousValue?: string;
  newValue?: string;
  taskId: string;
  createdDate: Dayjs;
};

class KanbanTaskStore {
  ApiStore: ApiStore;
  projectTasks: KanbanTask[] = [];
  kanbanViewTasks: KanbanTask[] = [];
  deletedProjectTasks: KanbanTask[] = [];
  projectStatuses: KanbanTaskStatus[] = [];
  attachments: Attachment[] = [];
  taskChanges: KanbanTaskChange[] = [];
  currentTask: KanbanTask | null = null;

  // Loaders variables
  private loadingCount = 0;
  isLoading: boolean = false;
  isTitleLoading: boolean = false;
  isDescriptionLoading: boolean = false;
  isStatusLoading: boolean = false;
  isTimeLogLoading: boolean = false;
  attachmentsLoading: boolean = false;
  isScheduledAndPriorityLoading: boolean = false;
  isAssigneeLoading: boolean = false;
  isCustomFieldsLoading: boolean = false;
  isAttachmentDeleting: boolean = false;
  isExportingTimeLogs: boolean = false;

  constructor(apiStore: ApiStore) {
    makeAutoObservable(this);
    this.ApiStore = apiStore;
  }

  taskTimeMetrics: TaskTimeMetrics | null = null;
  taskTimeLogs: TaskTimeLog[] = [];

  // Add this to your KanbanTaskStore.ts

  // Custom Fields properties
  projectCustomFields: any[] = [];
  taskCustomFieldValues: Record<string, any[]> = {}; // Map of taskId -> values

  //#region Setters
  setLoading = (loading: boolean): void => {
    if (loading) {
      this.loadingCount++;
    } else {
      this.loadingCount = Math.max(0, this.loadingCount - 1);
    }

    this.isLoading = this.loadingCount > 0;
  }
  //#endregion



  //#region Kanban Task Methods
  getProjectTasks = async (projectId: string): Promise<void> => {
    this.setLoading(true);
    try {
      const tasks = await this.ApiStore.KanbanTaskApiClient.getTasks(projectId);
      if (tasks) {
        this.projectTasks = tasks;
      }
    } finally {
      this.setLoading(false);
    }
  }

  getKanbanViewTasks = async (projectId: string): Promise<void> => {
    this.setLoading(true);
    try {
      const tasks = await this.ApiStore.KanbanTaskApiClient.getKanbanTasks(projectId);
      if (tasks) {
        this.kanbanViewTasks = tasks;
      }
    } finally {
      this.setLoading(false);
    }
  }

  getDeletedProjectTasks = async (projectId: string): Promise<void> => {
    this.setLoading(true);
    try {
      const tasks = await this.ApiStore.KanbanTaskApiClient.getDeletedTasks(projectId);
      if (tasks) {
        this.deletedProjectTasks = tasks;
      }
    } finally {
      this.setLoading(false);
    }
  }

  getTaskById = async (taskId: string): Promise<KanbanTask | null> => {
    this.setLoading(true);
    try {
      this.currentTask = null;
      const task = await this.ApiStore.KanbanTaskApiClient.getTaskById(taskId);
      this.currentTask = task;
      return task;
    } catch (error) {
      console.error("Failed to load task changes:", error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  getTaskChanges = async (taskId: string): Promise<void> => {
    this.setLoading(true);
    try {
      const changes = await this.ApiStore.KanbanTaskApiClient.getTaskChanges(taskId);
      this.taskChanges = changes;
    } catch (error) {
      console.error("Failed to load task changes:", error);
    } finally {
      this.setLoading(false);
    }
  }

  createTask = async (task: any): Promise<KanbanTask | null> => {
    this.setLoading(true);
    try {
      const payload = {
        ...task,
        summary: task?.description ? makeSummary(task.description) : ""
      };

      const result = await this.ApiStore.KanbanTaskApiClient.createTask(payload);
      if (result) {
        const fullTask = await this.ApiStore.KanbanTaskApiClient.getTaskDetails(result.id.toString());
        const taskToAdd = fullTask ? fullTask : result;
        
        this.projectTasks.push(taskToAdd);
        this.kanbanViewTasks.push(taskToAdd);
        
        return taskToAdd;
      }
      return null;
    } catch (error) {
      console.error("Error creating task:", error);
      return null;
    } finally {
      this.setLoading(false);
    }
  };


  updateTask = async (taskId: string, task: KanbanTask): Promise<void> => {
    this.setLoading(true);
    try {
      const originalTask = this.projectTasks.find(t => String(t.id) === String(taskId)) || this.kanbanViewTasks.find(t => String(t.id) === String(taskId));
      const assigneeChanged = originalTask &&
        String(originalTask.assigneeId) !== String(task.assigneeId);

      const result = await this.ApiStore.KanbanTaskApiClient.updateTask(taskId, task);

      if (result) {
        if (assigneeChanged && task.assigneeId) {
          const fullTaskDetails = await this.ApiStore.KanbanTaskApiClient.getTaskDetails(taskId);

          if (fullTaskDetails) {
            this.projectTasks = this.projectTasks.map(t =>
              String(t.id) === String(taskId) ? fullTaskDetails : t
            );
            this.kanbanViewTasks = this.kanbanViewTasks.map(t =>
              String(t.id) === String(taskId) ? fullTaskDetails : t
            );

            if ((this.projectTasks.length > 0 || this.kanbanViewTasks.length > 0) && result) {
              this.onTaskUpdated(fullTaskDetails);
            }
          } else {
            this.projectTasks = this.projectTasks.map(t =>
              String(t.id) === String(taskId) ? result : t
            );
            this.kanbanViewTasks = this.kanbanViewTasks.map(t =>
              String(t.id) === String(taskId) ? result : t
            );
          }
        } else {
          this.projectTasks = this.projectTasks.map(t =>
            String(t.id) === String(taskId) ? result : t
          );
          this.kanbanViewTasks = this.kanbanViewTasks.map(t =>
            String(t.id) === String(taskId) ? result : t
          );
        }
      }
    } catch (error) {
      console.error("Failed to update task:", error);
    } finally {
      this.setLoading(false);
    }
  }

  // Add this helper method for task update notifications
  onTaskUpdated = (task: KanbanTask) => {
    // This method can be used for any task update notifications
    // The fact that we're updating the task object will trigger mobx observers to update
    console.log(`Task ${task.id} updated with new details`);

    // You could add event emitter logic here if needed for component communication
  }

  updateTaskOrder = async (taskId: string, newIndex: number): Promise<void> => {
    this.setLoading(true);
    try {
      const updatedTask = await this.ApiStore.KanbanTaskApiClient.updateTaskOrder(taskId, newIndex);
      if (updatedTask) {
        // Re-fetch tasks for the project to refresh ordering across the board.
        await this.getProjectTasks(updatedTask.projectId);
        await this.getKanbanViewTasks(updatedTask.projectId);
      }
    } finally {
      this.setLoading(false);
    }
  }

  restoreTask = async (taskId: string): Promise<void> => {
    this.setLoading(true);
    try {
      const restored = await this.ApiStore.KanbanTaskApiClient.restoreTask(taskId);

      if (restored) {
        this.deletedProjectTasks = this.deletedProjectTasks.filter(t => String(t.id) !== String(taskId));

        this.projectTasks.push(restored);
        this.kanbanViewTasks.push(restored);

        if (this.currentTask?.id === restored.id) {
          this.currentTask = restored;
        }
      }
    } catch (error) {
      console.error("Failed to restore task:", error);
    } finally {
      this.setLoading(false);
    }
  }

  async bulkRestore(taskIds: string[]) {
    const result = await this.ApiStore.KanbanTaskApiClient.bulkRestore(taskIds);

    const restored = this.deletedProjectTasks.filter(t =>
      taskIds.includes(t.id.toString())
    );

    this.projectTasks.push(...restored);
    this.deletedProjectTasks = this.deletedProjectTasks.filter(
      t => !taskIds.includes(t.id.toString())
    );

    return result;
  }

  deleteTask = async (taskId: string): Promise<void> => {
    this.setLoading(true);
    try {
      const result = await this.ApiStore.KanbanTaskApiClient.deleteTask(taskId);
      if (result) {
        this.projectTasks = this.projectTasks.filter(task => task.id !== taskId);
        this.kanbanViewTasks = this.kanbanViewTasks.filter(task => task.id !== taskId);
      }
    } finally {
      this.setLoading(false);
    }
  }

  async bulkSoftDelete(taskIds: string[]) {
    const result = await this.ApiStore.KanbanTaskApiClient.bulkSoftDelete(taskIds);

    this.projectTasks = this.projectTasks.filter(t => !taskIds.includes(t.id.toString()));
    this.kanbanViewTasks = this.kanbanViewTasks.filter(t => !taskIds.includes(t.id.toString()));

    return result;
  }

  deleteTaskPermanent = async (taskId: string): Promise<void> => {
    this.setLoading(true);
    try {
      const result = await this.ApiStore.KanbanTaskApiClient.deleteTaskPermanent(taskId);
      if (result) {
        this.deletedProjectTasks = this.deletedProjectTasks.filter(task => task.id !== taskId);
      }
    } finally {
      this.setLoading(false);
    }
  }

  async bulkPermanentDelete(taskIds: string[]) {
    const result = await this.ApiStore.KanbanTaskApiClient.bulkPermanentDelete(taskIds);

    this.deletedProjectTasks = this.deletedProjectTasks.filter(t => !taskIds.includes(t.id.toString()));

    return result;
  }
  //#endregion



  //#region Task Movement Method
  async moveTask(taskId: string, toStatusId: string, toIndex: number): Promise<void> {
    this.setLoading(true);
    try {
      await this.ApiStore.KanbanTaskApiClient.moveTask(taskId, toStatusId, toIndex);
      const projId = this.projectTasks.find(t => String(t.id) === String(taskId))?.projectId 
        || this.kanbanViewTasks.find(t => String(t.id) === String(taskId))?.projectId;
      if (projId) {
        await this.getProjectTasks(String(projId));
        await this.getKanbanViewTasks(String(projId));
      }
    } finally {
      this.setLoading(false);
    }
  }
  //#endregion



  //#region Custom Fields Methods
  getProjectCustomFields = async (projectId: string): Promise<void> => {
    this.setLoading(true);
    try {
      const fields = await this.ApiStore.KanbanTaskApiClient.getProjectCustomFields(projectId);
      if (fields) {
        this.projectCustomFields = fields;
      }
    } catch (error) {
      console.error("Failed to load project custom fields:", error);
    } finally {
      this.setLoading(false);
    }
  }

  getTaskCustomFieldValues = async (taskId: string): Promise<void> => {
    if (!taskId) return;

    // this.setLoading(true); //todo not use main Load its blocking all page
    try {
      const values = await this.ApiStore.KanbanTaskApiClient.getTaskCustomFieldValues(taskId);
      if (values) {
        this.taskCustomFieldValues[taskId] = values;
      }
    } catch (error) {
      console.error("Failed to load task custom field values:", error);
    } finally {
      // this.setLoading(false);
    }
  }

  setTaskCustomFieldValue = async (taskId: string, fieldId: string, value: string, userId?: string): Promise<boolean> => {
    this.isCustomFieldsLoading = true;
    try {
      const result = await this.ApiStore.KanbanTaskApiClient.setTaskCustomFieldValue(taskId, fieldId, value, userId);
      if (result) {
        // Update local state
        await this.getTaskCustomFieldValues(taskId);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to set custom field value:", error);
      return false;
    } finally {
      this.isCustomFieldsLoading = false;
    }
  }

  updateTaskCustomFieldValues = async (taskId: string, fieldValues: Record<string, string>): Promise<boolean> => {
    this.setLoading(true);
    try {
      const result = await this.ApiStore.KanbanTaskApiClient.updateTaskCustomFieldValues(taskId, fieldValues);
      if (result) {
        // Update local state
        await this.getTaskCustomFieldValues(taskId);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to update task custom field values:", error);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  createCustomField = async (field: any): Promise<any> => {
    this.setLoading(true);
    try {
      const result = await this.ApiStore.KanbanTaskApiClient.createCustomField(field);
      if (result) {
        // Update local state
        this.projectCustomFields.push(result);
        // Sort by display order
        this.projectCustomFields.sort((a, b) => a.displayOrder - b.displayOrder);
        return result;
      }
      return null;
    } catch (error) {
      console.error("Failed to create custom field:", error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  updateCustomField = async (fieldId: string, field: any): Promise<boolean> => {
    this.setLoading(true);
    try {
      const result = await this.ApiStore.KanbanTaskApiClient.updateCustomField(fieldId, field);

      const index = this.projectCustomFields.findIndex(f => f.id.toString() === fieldId.toString());
      if (index !== -1) {
        const updatedField = {
          ...this.projectCustomFields[index],
          ...field,
          ...(result || {})
        };

        this.projectCustomFields = [
          ...this.projectCustomFields.slice(0, index),
          updatedField,
          ...this.projectCustomFields.slice(index + 1)
        ];
      }

      return true;
    } catch (error) {
      console.error("Failed to update custom field:", error);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  deleteCustomField = async (fieldId: string): Promise<boolean> => {
    this.setLoading(true);
    try {
      const result = await this.ApiStore.KanbanTaskApiClient.deleteCustomField(fieldId);
      if (result) {
        // Update local state
        this.projectCustomFields = this.projectCustomFields.filter(f => f.id.toString() !== fieldId.toString());
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to delete custom field:", error);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  // Helper methods for custom fields
  getFieldValueForTask = (taskId: string, fieldId: string): any => {
    const values = this.taskCustomFieldValues[taskId] || [];
    const valueObj = values.find(v => v.customFieldId.toString() === fieldId.toString());
    return valueObj ? valueObj.value : null;
  }

  getFieldDefinition = (fieldId: string): any => {
    return this.projectCustomFields.find(f => f.id.toString() === fieldId.toString());
  }
  //#endregion



  //#region Time Tracking Methods
  getTaskTimeMetrics = async (taskId: string): Promise<TaskTimeMetrics | null> => {
    // this.setLoading(true);  //todo not use main loading its block page
    try {
      const metrics = await this.ApiStore.KanbanTaskApiClient.getTaskTimeMetrics(taskId);
      this.taskTimeMetrics = metrics;
      return metrics;
    } catch (error) {
      console.error("Failed to load task time metrics:", error);
      return null;
    } finally {
      // this.setLoading(false);
    }
  }

  updateEstimatedTime = async (taskId: string, estimatedTime: string): Promise<boolean> => {
    this.setLoading(true);
    try {
      const result = await this.ApiStore.KanbanTaskApiClient.updateEstimatedTime(taskId, estimatedTime);

      if (result) {
        this.taskTimeMetrics = result;

        // Find the task in projectTasks
        const taskIndex = this.projectTasks.findIndex(t => t.id === taskId);
        if (taskIndex !== -1) {
          const updatedTask = { ...this.projectTasks[taskIndex], estimatedTime };
          this.projectTasks = [
            ...this.projectTasks.slice(0, taskIndex),
            updatedTask,
            ...this.projectTasks.slice(taskIndex + 1)
          ];
        }

        // Find the task in kanbanViewTasks
        const kanbanTaskIndex = this.kanbanViewTasks.findIndex(t => t.id === taskId);
        if (kanbanTaskIndex !== -1) {
          const updatedKanbanTask = { ...this.kanbanViewTasks[kanbanTaskIndex], estimatedTime };
          this.kanbanViewTasks = [
            ...this.kanbanViewTasks.slice(0, kanbanTaskIndex),
            updatedKanbanTask,
            ...this.kanbanViewTasks.slice(kanbanTaskIndex + 1)
          ];
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to update estimated time:", error);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  logTimeForTask = async (
    taskId: string,
    time: string,
    description: string,
    userId?: string,
    endDate?: string
  ): Promise<boolean> => {
    //
    this.isTimeLogLoading = true;
    try {
      const result = await this.ApiStore.KanbanTaskApiClient.logTimeForTask(
        taskId, time, description, userId, endDate
      );

      if (result) {
        // Update metrics and logs with the returned data
        if (result.metrics) {
          this.taskTimeMetrics = result.metrics;
        } else {
          // If metrics weren't returned, fetch them
          await this.getTaskTimeMetrics(taskId);
        }

        // Refresh the time logs
        await this.getTaskTimeLogs(taskId);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to log time for task:", error);
      return false;
    } finally {
      this.isTimeLogLoading = false;
    }
  }

  getTaskTimeLogs = async (taskId: string): Promise<TaskTimeLog[]> => {
    this.isTimeLogLoading = true;
    try {
      const logs = await this.ApiStore.KanbanTaskApiClient.getTaskTimeLogs(taskId);
      this.taskTimeLogs = logs;
      return logs;
    } catch (error) {
      console.error("Failed to load task time logs:", error);
      return [];
    } finally {
      this.isTimeLogLoading = false;
    }
  }
  //#endregion



  //#region Attachment Methods
  getTaskAttachments = async (taskId: string): Promise<Attachment[]> => {
    this.attachmentsLoading = true;
    try {
      console.log(`Store: Fetching attachments for task ${taskId}`);
      const att = await this.ApiStore.KanbanTaskApiClient.getTaskAttachments(taskId);

      // Ensure we always have an array, even if the API returns null
      this.attachments = Array.isArray(att) ? att : [];

      console.log(`Store: Retrieved ${this.attachments.length} attachments`);
      return this.attachments;
    } catch (error) {
      console.error("Store: Error fetching attachments:", error);
      this.attachments = [];
      return [];
    } finally {
      this.attachmentsLoading = false;
    }
  };

  // Improved upload attachment method
  uploadTaskAttachment = async (taskId: string, file: File, confirmation: boolean = false): Promise<Attachment | null> => {
    this.setLoading(true);
    try {
      console.log(`Store: Uploading ${file.name} to task ${taskId}`);

      // First verify the task exists in our local cache
      const taskExists = this.kanbanViewTasks.some(task => task.id.toString() === taskId);
      console.log(JSON.parse(JSON.stringify(this.kanbanViewTasks)))
      if (!taskExists) {
        console.error(this.kanbanViewTasks.length);
        console.error(`Store: Task ${taskId} not found in project tasks`);
        throw new Error('Task not found');
      }

      const result = await this.ApiStore.KanbanTaskApiClient.uploadTaskAttachment(taskId, file, confirmation);

      if (result) {
        console.log('Store: Upload successful:', result);
        // Add to local attachments array if not already present
        if (!this.attachments.some(a => a.publicId === result.publicId)) {
          this.attachments.push(result);
        }
        return result;
      } else {
        console.error('Store: Upload failed, no result returned');
        return null;
      }
    } catch (error) {
      console.error("Store: Error uploading attachment:", error);
      throw error; // Rethrow to handle in component
    } finally {
      this.setLoading(false);
    }
  };

  // Improved delete attachment method
  deleteAttachment = async (publicId: string, taskId: string): Promise<boolean> => {
    this.isAttachmentDeleting = true;
    try {
      console.log(`Store: Deleting attachment ${publicId}`);
      const result = await this.ApiStore.KanbanTaskApiClient.deleteFile(publicId);

      if (result) {
        console.log('Store: Delete successful');
        // Update attachments in the store
        this.attachments = this.attachments.filter((att) => att.publicId !== publicId);
        return true;
      } else {
        console.error('Store: Delete failed');
        return false;
      }
    } catch (error) {
      console.error("Store: Error deleting attachment:", error);
      return false;
    } finally {
      this.isAttachmentDeleting = false;
    }
  };
  //#endregion



  //#region Methods to update specific task fields
  updateTaskStatus = async (taskId: string, statusId: string): Promise<void> => {
    this.isStatusLoading = true;
    try {
      const updatedTask = await this.ApiStore.KanbanTaskApiClient.updateTaskStatus(taskId, statusId);
      if (updatedTask) {
        // Replace old task with the new, fully-populated task
        this.projectTasks = this.projectTasks.map((t) =>
          t.id === updatedTask.id ? updatedTask : t
        );
      }
    } finally {
      this.isStatusLoading = false;
    }
  }

  updateTaskTitle = async (taskId: string, title: string): Promise<void> => {
    const originalTasks = [...this.projectTasks];
    this.projectTasks = this.projectTasks.map(t =>
      t.id === taskId ? { ...t, title } : t
    );

    this.isTitleLoading = true;
    try {
      const updatedTask = await this.ApiStore.KanbanTaskApiClient.updateTaskTitle(taskId, title);
      if (updatedTask) {
        this.projectTasks = this.projectTasks.map(t =>
          t.id === updatedTask.id ? updatedTask : t
        );
      }
    } catch (error) {
      this.projectTasks = originalTasks;
      throw error;
    } finally {
      this.isTitleLoading = false;
    }
  }

  updateTaskDescription = async (taskId: string, description: string): Promise<void> => {
    this.isDescriptionLoading = true;
    try {
      const updatedTask = await this.ApiStore.KanbanTaskApiClient.updateTaskDescription(taskId, description);
      if (updatedTask) {
        const summary = makeSummary(description);
        const toPersist = { ...updatedTask, summary };

        const serverTask = await this.ApiStore.KanbanTaskApiClient.updateTask(taskId, toPersist as KanbanTask);

        const finalTask = serverTask ?? toPersist;

        this.projectTasks = this.projectTasks.map((t) =>
          String(t.id) === String(finalTask.id) ? (finalTask as KanbanTask) : t
        );
      }
    } catch (e) {
      console.error("Failed to update task description:", e);
    } finally {
      this.isDescriptionLoading = false;
    }
  };

  updateTaskScheduledAndPriority = async (taskId: string, scheduledAndPriority: ScheduledAndPriorityUpdate): Promise<void> => {
    this.isScheduledAndPriorityLoading = true;
    try {
      const updatedTask = await this.ApiStore.KanbanTaskApiClient.updateTaskScheduledAndPriority(taskId, scheduledAndPriority);
      if (updatedTask) {
        // Replace old task with the new, fully-populated task
        this.projectTasks = this.projectTasks.map((t) =>
          t.id === updatedTask.id ? updatedTask : t
        );
      }
    } finally {
      this.isScheduledAndPriorityLoading = false;
    }
  }

  updateTaskAssignee = async (taskId: string, assigneeId?: string): Promise<void> => {
    this.isAssigneeLoading = true;
    try {
      const updatedTask = await this.ApiStore.KanbanTaskApiClient.updateTaskAssignee(taskId, assigneeId);
      if (updatedTask) {
        // Replace old task with the new, fully-populated task
        this.projectTasks = this.projectTasks.map((t) =>
          t.id === updatedTask.id ? updatedTask : t
        );
      }
    } finally {
      this.isAssigneeLoading = false;
    }
  }
  //#endregion



  //#region Task Status Methods
  getProjectStatuses = async (projectId: string): Promise<void> => {
    this.setLoading(true);
    try {
      const statuses = await this.ApiStore.KanbanTaskApiClient.getProjectStatuses(projectId);
      if (statuses) {
        this.projectStatuses = statuses.sort((a, b) => a.index - b.index);
      }
    } catch (error) {
      console.error("Failed to load project statuses:", error);
    } finally {
      this.setLoading(false);
    }
  }

  createStatus = async (name: string, projectId: string, isFinalStatus: boolean): Promise<KanbanTaskStatus | null> => {
    this.setLoading(true);
    try {
      // Calculate next index
      const nextIndex = this.projectStatuses.length;

      const result = await this.ApiStore.KanbanTaskApiClient.createStatus(name, projectId, nextIndex, isFinalStatus);
      if (result) {
        this.projectStatuses.push(result);
        this.projectStatuses = this.projectStatuses.sort((a, b) => a.index - b.index);
        return result;
      }
      return null;
    } catch (error) {
      console.error("Failed to create status:", error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  updateStatus = async (statusId: string, name: string, isFinalStatus: boolean): Promise<KanbanTaskStatus | null> => {
    this.setLoading(true);
    try {
      const status = this.projectStatuses.find(s => s.id === statusId);
      if (!status) {
        throw new Error(`Status with ID ${statusId} not found`);
      }

      const updatedStatus = { ...status, name, isFinalStatus };
      const result = await this.ApiStore.KanbanTaskApiClient.updateStatus(statusId, updatedStatus);

      if (result) {
        this.projectStatuses = this.projectStatuses.map(s =>
          s.id === statusId ? result : s
        );
        return result;
      }
      return null;
    } catch (error) {
      console.error("Failed to update status:", error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  updateStatusOrder = async (statusId: string, newIndex: number): Promise<void> => {
    this.setLoading(true);
    try {
      await this.ApiStore.KanbanTaskApiClient.updateStatusOrder(statusId, newIndex);
      // Refresh the statuses from the server to get the updated order
      const projectId = this.projectStatuses.find(s => s.id === statusId)?.projectId;
      if (projectId) {
        await this.getProjectStatuses(projectId.toString());
      }
    } catch (error) {
      console.error("Failed to update status order:", error);
    } finally {
      this.setLoading(false);
    }
  }

  deleteStatus = async (statusId: string): Promise<boolean> => {
    this.setLoading(true);
    try {
      const result = await this.ApiStore.KanbanTaskApiClient.deleteStatus(statusId);
      if (result) {
        // Filter out the deleted status
        this.projectStatuses = this.projectStatuses.filter(s => s.id !== statusId);

        // Filter out tasks that had this status
        this.projectTasks = this.projectTasks.filter(t => t.statusId !== statusId);
        this.kanbanViewTasks = this.kanbanViewTasks.filter(t => t.statusId !== statusId);

        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to delete status:", error);
      return false;
    } finally {
      this.setLoading(false);
    }
  }
  //#endregion



  //#region Methods For Export Files
  extractFilename(disposition?: string): string | null {
    if (!disposition) return null;
    const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(disposition);
    if (star?.[1]) return decodeURIComponent(star[1].replace(/^["']|["']$/g, ''));
    const plain = /filename=(?:UTF-8'')?([^;]+)/i.exec(disposition);
    if (plain?.[1]) return plain[1].trim().replace(/^["']|["']$/g, '');
    return null;
  }

  downloadBlob(potentialBlob: any, filename = 'export.csv') {
    const blob = this.toCsvBlob(potentialBlob, 'text/csv');

    if (!blob || blob.size === 0) {
      console.warn('Empty blob - nothing to load');
      return;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  exportTimeLogsAndDownload = async (params: ExportTimeLogsParams): Promise<boolean> => {
    runInAction(() => { this.isExportingTimeLogs = true; });
    try {
      const res = await this.ApiStore.KanbanTaskApiClient.exportProjectTimeLogs(params);

      console.log('exportProjectTimeLogs result:', {
        type: typeof res,
        ctor: (res as any)?.constructor?.name,
        hasBlob: !!(res as any)?.blob,
        blobCtor: (res as any)?.blob?.constructor?.name
      });

      if (res instanceof Blob) {
        const fallback = `project-time-logs_${dayjs().format('YYYY-MM-DD')}.csv`;
        this.downloadBlob(res, fallback);
        return true;
      }

      if (res && typeof res === 'object' && 'blob' in (res as any)) {
        const r: any = res;
        const cd = r.headers?.['content-disposition'];
        const fallback = `project-time-logs_${dayjs().format('YYYY-MM-DD')}.csv`;
        const filename = this.extractFilename(cd) || fallback;
        this.downloadBlob(r.blob, filename);
        return true;
      }

      if (typeof res === 'string' || res instanceof ArrayBuffer || ArrayBuffer.isView(res)) {
        const fallback = `project-time-logs_${dayjs().format('YYYY-MM-DD')}.csv`;
        this.downloadBlob(res, fallback);
        return true;
      }
      if (res && typeof res === 'object' && 'data' in res) {
        const r: any = res;
        const cd = r.headers?.['content-disposition'];
        const fallback = `project-time-logs_${dayjs().format('YYYY-MM-DD')}.csv`;
        const filename = this.extractFilename(cd) || fallback;
        this.downloadBlob(r.data, filename);
        return true;
      }

      console.error('exportTimeLogsAndDownload: unsupported response shape', res);
      return false;
    } catch (e) {
      console.error('exportTimeLogsAndDownload error:', e);
      return false;
    } finally {
      runInAction(() => { this.isExportingTimeLogs = false; });
    }
  };

  toCsvBlob(content: unknown, mime = "text/csv"): Blob {
    if (content instanceof Blob) return content;

    if (content instanceof ArrayBuffer) return new Blob([content], { type: mime });

    if (ArrayBuffer.isView(content)) {
      const view = content as ArrayBufferView;
      const copy = new Uint8Array(view.buffer, view.byteOffset, view.byteLength).slice();
      return new Blob([copy], { type: mime });
    }

    if (content && typeof content === "object") {
      const anyObj = content as any;
      if (typeof anyObj.byteLength === "number" && anyObj.byteLength >= 0) {
        const copy = new Uint8Array(new Uint8Array(anyObj as ArrayBufferLike)).slice();
        return new Blob([copy], { type: mime });
      }
      if (Array.isArray(content)) {
        const u8 = Uint8Array.from(content as number[]);
        return new Blob([u8], { type: mime });
      }
    }
    if (typeof content === "string") return new Blob([content], { type: mime });
    return new Blob([String(content ?? "")], { type: mime });
  }
  //#endregion
}

export default KanbanTaskStore;